import { useContext, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AuthContext from "../store/auth-context";
import { GET } from "../service/CRUDService";
import EntityConstants from "../configs/EntityConstants";

export default function useSidebarNavigation(allNavItems) {
  const location = useLocation();
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const { userType, setSidebarComponents: persistSidebarComponents } =
    authContext;

  const [sidebarComponents, setSidebarComponents] = useState(
    () =>
      (Array.isArray(authContext.sidebarComponents) &&
        authContext.sidebarComponents) ||
      []
  );
  const [initialRedirectDone, setInitialRedirectDone] = useState(false);

  const getValidPaths = useCallback(
    (components) => {
      return (components || [])
        .filter(
          (key) =>
            allNavItems[key] &&
            allNavItems[key].allowedRoles.includes(userType)
        )
        .map((key) => allNavItems[key].to);
    },
    [allNavItems, userType]
  );

  const getFirstValidComponent = useCallback(
    (components) => {
      const validComponents = (components || [])
        .filter(
          (key) =>
            allNavItems[key] &&
            allNavItems[key].allowedRoles.includes(userType)
        )
        .map((key) => allNavItems[key]);
      return validComponents.length > 0 ? validComponents[0] : null;
    },
    [allNavItems, userType]
  );

  const isOnValidPage = useCallback(
    (components) => {
      const currentPath = location.pathname;
      const validPaths = getValidPaths(components);
      return validPaths.some((path) => currentPath.startsWith(path));
    },
    [getValidPaths, location.pathname]
  );

  const handleInitialRedirect = useCallback(
    (components) => {
      if (isOnValidPage(components)) return;
      const firstValidComponent = getFirstValidComponent(components);
      if (firstValidComponent) {
        navigate(firstValidComponent.to);
      }
    },
    [getFirstValidComponent, isOnValidPage, navigate]
  );

  const fetchCurrentUser = useCallback(async () => {
    try {
      const response = await GET(EntityConstants.VERIFIER_PERMISSIONS);
      const data = await response.json();
      const components = Array.isArray(data.sidebar_components)
        ? data.sidebar_components
        : [];
      setSidebarComponents(components);
      persistSidebarComponents(components);
      if (!initialRedirectDone) {
        handleInitialRedirect(components);
        setInitialRedirectDone(true);
      }
    } catch (error) {
      console.error("Error fetching permissions data:", error);
    }
  }, [handleInitialRedirect, initialRedirectDone, persistSidebarComponents]);

  return {
    sidebarComponents,
    setSidebarComponents,
    initialRedirectDone,
    setInitialRedirectDone,
    fetchCurrentUser,
  };
}
